import { FunctionComponent, useRef } from 'react'
import ReactQuill from 'react-quill-new'
import { Button, Form } from 'react-bootstrap'
import { quillModules, getSemanticHtml } from 'Frontend/scripts/quillModules'
import { createWorkroomPost } from 'Frontend/scripts/services/workrooms.service'
import { useForm, Controller } from 'react-hook-form'
import { IAntiforgeryToken, IIdObject } from 'Frontend/scripts/models/general.model'
import { getAntiforgeryToken } from 'Frontend/scripts/services/general.service'
import { useTranslation } from 'react-i18next'

interface CreateNewWorkroomPostProps {
  id: number
  callback: (_activePage: number, postId?: number) => void | null
}

const CreateNewWorkroomPost: FunctionComponent<CreateNewWorkroomPostProps> = ({ id, callback }) => {
  const quillRef = useRef<ReactQuill | null>(null)

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      heading: '',
      text: '',
      emailNotification: true,
    },
  })
  const { t } = useTranslation()

  const onSubmit = (data: any) => {
    const mainBodyWithCorrectHTML = getSemanticHtml(quillRef)
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      createWorkroomPost(token.csrfToken, id, data.heading, mainBodyWithCorrectHTML, data.emailNotification).then(
        (postId: IIdObject) => {
          callback(1, parseInt(postId.id))
        },
      )
    })
  }

  return (
    <div className="mt-3 livstecknet__form-area p-3">
      <h2>{t('new-post')}</h2>
      <Form noValidate onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column gap-3">
        <Form.Group>
          <Form.Label htmlFor="inputHeading">{t('heading')}</Form.Label>
          <Form.Control
            id="inputHeading"
            type="text"
            {...register('heading', {
              required: t('error-add-heading'),
              maxLength: {
                value: 255,
                message: t('error-text-to-long'),
              },
            })}
          />
          {errors.heading && <Form.Text className="text-danger">{errors.heading.message}</Form.Text>}
        </Form.Group>

        <Form.Group>
          <Form.Label htmlFor="inputText">{t('text')}</Form.Label>
          <Controller
            name="text"
            control={control}
            rules={{
              validate: (value) => value?.replace(/<(.|\n)*?>/g, '').trim().length > 0 || t('error-text'),
            }}
            render={({ field }) => (
              <ReactQuill
                {...field}
                ref={quillRef}
                id="inputDescription"
                className="notice__quill-editor"
                theme="snow"
                modules={quillModules}
                onChange={(value) => setValue('text', value, { shouldValidate: true })}
              />
            )}
          />
          {errors.text && <Form.Text className="text-danger">{errors.text.message}</Form.Text>}
        </Form.Group>

        <Form.Group>
          <Form.Check
            type="checkbox"
            id="email-reminders"
            label={t('workrooms.posts.send-email-notification')}
            {...register('emailNotification')}
          />
        </Form.Group>

        <div className="d-flex gap-3">
          <Button variant="secondary" type="submit">
            {t('create-post')}
          </Button>
          <Button variant="outline-secondary" type="button" onClick={() => callback(0)}>
            {t('abort')}
          </Button>
        </div>
      </Form>
    </div>
  )
}

export default CreateNewWorkroomPost
